import { useState, useEffect, useCallback } from 'react';
import { type Venue, type User, isAdmin } from '@peles/shared';
import { useVenue, useUpdateVenue, useDeactivateVenue } from '../../hooks/useVenues';
import { useUsers } from '../../hooks/useUsers';
import { useVendors } from '../../hooks/useVendors';
import { GoogleAddressAutocomplete } from '../common/GoogleAddressAutocomplete';
import { useDialog } from '../../context/DialogContext';
import { PhoneInput } from '../common/PhoneInput';

interface VenueDetailPanelProps {
  venueId: string;
  onClose: () => void;
  onUnsavedChange: (dirty: boolean) => void;
  currentUser: User;
}

interface FormState {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  email: string;
  contactId: string;
  vendorId: string;
  capacity: string;
  priceRange: string;
  notes: string;
  isActive: boolean;
}

function venueToFormState(venue: Venue): FormState {
  return {
    name: venue.name,
    address: venue.address || '',
    city: venue.city || '',
    state: venue.state || '',
    zipCode: venue.zipCode || '',
    country: venue.country || '',
    phone: venue.phone || '',
    email: venue.email || '',
    contactId: venue.contactId || '',
    vendorId: venue.vendorId || '',
    capacity: venue.capacity?.toString() || '',
    priceRange: venue.priceRange || '',
    notes: venue.notes || '',
    isActive: venue.isActive,
  };
}

function isDirty(form: FormState, original: FormState): boolean {
  return (
    form.name !== original.name ||
    form.address !== original.address ||
    form.city !== original.city ||
    form.state !== original.state ||
    form.zipCode !== original.zipCode ||
    form.country !== original.country ||
    form.phone !== original.phone ||
    form.email !== original.email ||
    form.contactId !== original.contactId ||
    form.vendorId !== original.vendorId ||
    form.capacity !== original.capacity ||
    form.priceRange !== original.priceRange ||
    form.notes !== original.notes ||
    form.isActive !== original.isActive
  );
}

export function VenueDetailPanel({
  venueId,
  onClose,
  onUnsavedChange,
  currentUser,
}: VenueDetailPanelProps) {
  const { confirm } = useDialog();
  const { data: venue, isLoading } = useVenue(venueId);
  const updateVenue = useUpdateVenue();
  const deactivateVenue = useDeactivateVenue();
  const { data: users = [] } = useUsers({ isActive: true });
  const { data: vendors = [] } = useVendors();

  const [form, setForm] = useState<FormState>({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phone: '',
    email: '',
    contactId: '',
    vendorId: '',
    capacity: '',
    priceRange: '',
    notes: '',
    isActive: true,
  });
  const [originalForm, setOriginalForm] = useState<FormState>(form);
  const [saved, setSaved] = useState(false);

  const currentUserIsAdmin = isAdmin(currentUser.systemRole);

  // Initialize form when venue data loads
  useEffect(() => {
    if (venue) {
      const state = venueToFormState(venue);
      setForm(state);
      setOriginalForm(state);
      setSaved(false);
    }
  }, [venue]);

  // Track dirty state
  const dirty = isDirty(form, originalForm);
  useEffect(() => {
    onUnsavedChange(dirty);
  }, [dirty, onUnsavedChange]);

  const handleSave = useCallback(() => {
    const updates: Record<string, unknown> = {};
    if (form.name !== originalForm.name) updates.name = form.name;
    if (form.address !== originalForm.address) updates.address = form.address || null;
    if (form.city !== originalForm.city) updates.city = form.city || null;
    if (form.state !== originalForm.state) updates.state = form.state || null;
    if (form.zipCode !== originalForm.zipCode) updates.zipCode = form.zipCode || null;
    if (form.country !== originalForm.country) updates.country = form.country || null;
    if (form.phone !== originalForm.phone) updates.phone = form.phone || null;
    if (form.email !== originalForm.email) updates.email = form.email || null;
    if (form.contactId !== originalForm.contactId) updates.contactId = form.contactId || null;
    if (form.vendorId !== originalForm.vendorId) updates.vendorId = form.vendorId || null;
    if (form.capacity !== originalForm.capacity) {
      updates.capacity = form.capacity ? parseInt(form.capacity, 10) : null;
    }
    if (form.priceRange !== originalForm.priceRange) updates.priceRange = form.priceRange || null;
    if (form.notes !== originalForm.notes) updates.notes = form.notes || null;
    if (form.isActive !== originalForm.isActive) updates.isActive = form.isActive;

    updateVenue.mutate(
      { id: venueId, updates },
      {
        onSuccess: (updated) => {
          const state = venueToFormState(updated);
          setForm(state);
          setOriginalForm(state);
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
      },
    );
  }, [form, originalForm, venueId, updateVenue]);

  const handleDeactivate = useCallback(async () => {
    if (
      await confirm({
        title: 'Deactivate Venue',
        message: `Deactivate venue "${venue?.name}"? This action cannot be undone.`,
        confirmLabel: 'Deactivate',
      })
    ) {
      deactivateVenue.mutate(venueId);
    }
  }, [venueId, venue?.name, deactivateVenue, confirm]);

  if (isLoading) {
    return (
      <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white">
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white">
        <p className="text-gray-500 text-sm">Venue not found</p>
      </div>
    );
  }

  return (
    <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white overflow-auto">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-lg font-semibold flex-1">Venue Details</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      {saved && (
        <div className="mb-4 px-3 py-2 bg-green-50 text-green-700 text-sm rounded">
          Changes saved successfully
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label
            htmlFor="detail-venue-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Name
          </label>
          <input
            id="detail-venue-name"
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label
            htmlFor="detail-venue-address"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Address
          </label>
          <GoogleAddressAutocomplete
            id="detail-venue-address"
            value={form.address}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            onAddressSelect={(fields) =>
              setForm((f) => ({
                ...f,
                city: fields.city || f.city,
                state: fields.state || f.state,
                zipCode: fields.zipCode || f.zipCode,
                country: fields.country || f.country,
              }))
            }
            placeholder="Enter address"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="detail-venue-city"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              City
            </label>
            <input
              id="detail-venue-city"
              type="text"
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              placeholder="Enter city"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="detail-venue-state"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              State
            </label>
            <input
              id="detail-venue-state"
              type="text"
              value={form.state}
              onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
              placeholder="Enter state"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="detail-venue-zip"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              ZIP Code
            </label>
            <input
              id="detail-venue-zip"
              type="text"
              value={form.zipCode}
              onChange={(e) => setForm((f) => ({ ...f, zipCode: e.target.value }))}
              placeholder="Enter ZIP"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="detail-venue-country"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Country
            </label>
            <input
              id="detail-venue-country"
              type="text"
              value={form.country}
              onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              placeholder="Enter country"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="detail-venue-contact"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Contact
          </label>
          <select
            id="detail-venue-contact"
            value={form.contactId}
            onChange={(e) => setForm((f) => ({ ...f, contactId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">No contact</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="detail-venue-vendor"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Venue Operator (Vendor)
          </label>
          <select
            id="detail-venue-vendor"
            value={form.vendorId}
            onChange={(e) => setForm((f) => ({ ...f, vendorId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">No vendor</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.businessName}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="detail-venue-phone"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Phone
            </label>
            <PhoneInput
              id="detail-venue-phone"
              value={form.phone}
              onChange={(phone) => setForm((f) => ({ ...f, phone }))}
              placeholder="Enter phone"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="detail-venue-email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              id="detail-venue-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="Enter email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="detail-venue-capacity"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Capacity
            </label>
            <input
              id="detail-venue-capacity"
              type="number"
              value={form.capacity}
              onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
              placeholder="Enter capacity"
              min="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="detail-venue-price"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Price Range
            </label>
            <input
              id="detail-venue-price"
              type="text"
              value={form.priceRange}
              onChange={(e) => setForm((f) => ({ ...f, priceRange: e.target.value }))}
              placeholder="e.g. $$-$$$"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="detail-venue-notes"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Notes
          </label>
          <textarea
            id="detail-venue-notes"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Enter notes"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
          <p className="text-sm text-gray-600 px-3 py-2 bg-gray-50 rounded-md">
            {new Date(venue.createdAt).toLocaleDateString()}
          </p>
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={handleSave}
          disabled={!dirty || updateVenue.isPending}
          className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {updateVenue.isPending ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>

      {/* Danger Zone */}
      {currentUserIsAdmin && venue.isActive && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-red-600 mb-3">Danger Zone</h3>
          <button
            onClick={handleDeactivate}
            disabled={deactivateVenue.isPending}
            className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {deactivateVenue.isPending ? 'Deactivating...' : 'Deactivate Venue'}
          </button>
        </div>
      )}
    </div>
  );
}
